import React, { useState } from 'react';

const RondasManagement = ({
  rondas,
  entities,
  volunteers,
  onCreateRonda,
  onUpdateRonda,
  setIsEditing
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRonda, setSelectedRonda] = useState(null);
  const [formData, setFormData] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="badge approved">Ativo</span>;
    } else {
      return <span className="badge suspended">Inativo</span>;
    }
  };

  const handleCreateRonda = () => {
    setFormData({
      id: '',  // Código da ronda (6 caracteres)
      nome: '',
      entidade_id: ''
    });
    setShowCreateModal(true);
    setIsEditing(true);
  };

  const handleEditRonda = (ronda) => {
    setSelectedRonda(ronda);
    setFormData({
      nome: ronda.nome || '',
      entidade_id: ronda.entidade_id || ''
    });
    setShowEditModal(true);
    setIsEditing(true);
  };


  const submitCreateRonda = async () => {
    if (!formData.id || !formData.nome) {
      alert('Código e nome são obrigatórios');
      return;
    }

    if (formData.id.length !== 6) {
      alert('Código deve ter exatamente 6 caracteres');
      return;
    }

    try {
      await onCreateRonda(formData);
      setShowCreateModal(false);
      setIsEditing(false);
      setFormData({});
    } catch (error) {
      alert('Erro ao criar ronda: ' + (error.response?.data?.message || error.message));
    }
  };

  const submitEditRonda = async () => {
    if (!formData.nome) {
      alert('Nome é obrigatório');
      return;
    }

    try {
      await onUpdateRonda(selectedRonda.id, formData);
      setShowEditModal(false);
      setIsEditing(false);
      setSelectedRonda(null);
      setFormData({});
    } catch (error) {
      alert('Erro ao atualizar ronda: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggleStatus = async (ronda) => {
    const action = ronda.ativo ? 'desativar' : 'ativar';
    const confirmMessage = `Tem certeza que deseja ${action} a ronda ${ronda.nome}?`;

    if (window.confirm(confirmMessage)) {
      try {
        await onUpdateRonda(ronda.id, { ...ronda, ativo: !ronda.ativo });
      } catch (error) {
        alert(`Erro ao ${action} ronda: ` + (error.response?.data?.message || error.message));
      }
    }
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setIsEditing(false);
    setSelectedRonda(null);
    setFormData({});
  };

  const availableVolunteers = volunteers?.filter(v => 
    v.volunteer_status === 'approved' && v.ativo
  ) || [];

  return (
    <div className="rondas-management">
      <div className="card-header">
        <h3>Gestão de Rondas</h3>
        <button className="btn btn-primary" onClick={handleCreateRonda}>
          Nova Ronda
        </button>
      </div>

      <div className="card-content">
        {rondas?.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma ronda encontrada</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Entidade</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rondas?.map(ronda => (
                  <tr key={ronda.id}>
                    <td><strong>{ronda.id}</strong></td>
                    <td>{ronda.nome}</td>
                    <td>
                      {entities?.find(e => e.id === ronda.entidade_id)?.nome || 'N/A'}
                    </td>
                    <td>{getStatusBadge(ronda.ativo ? 'active' : 'inactive')}</td>
                    <td>
                      <div className="action-buttons" style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => handleEditRonda(ronda)}
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          className={`btn btn-sm ${ronda.ativo ? 'btn-warning' : 'btn-success'}`}
                          onClick={() => handleToggleStatus(ronda)}
                          title={ronda.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {ronda.ativo ? '🚫' : '✅'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Ronda Modal */}
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Nova Ronda</h4>
            <form onSubmit={(e) => { e.preventDefault(); submitCreateRonda(); }}>
              <div className="form-group">
                <label>Código da Ronda * (6 caracteres)</label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value.toUpperCase().slice(0, 6) })}
                  placeholder="Ex: CDP001, LAR002"
                  maxLength={6}
                  required
                  style={{ textTransform: 'uppercase' }}
                />
                <small className="form-text">
                  Código único de 6 caracteres (Ex: entidade + número)
                </small>
              </div>
              <div className="form-group">
                <label>Nome da Ronda *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome descritivo da ronda"
                  required
                />
              </div>
              <div className="form-group">
                <label>Entidade</label>
                <select
                  value={formData.entidade_id}
                  onChange={(e) => setFormData({ ...formData, entidade_id: e.target.value })}
                >
                  <option value="">Selecionar entidade</option>
                  {entities?.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.id} - {entity.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Criar Ronda
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Ronda Modal */}
      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h4>Editar Ronda: {selectedRonda?.id}</h4>
            <form onSubmit={(e) => { e.preventDefault(); submitEditRonda(); }}>
              <div className="form-group">
                <label>Nome da Ronda *</label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Entidade</label>
                <select
                  value={formData.entidade_id}
                  onChange={(e) => setFormData({ ...formData, entidade_id: e.target.value })}
                >
                  <option value="">Selecionar entidade</option>
                  {entities?.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.id} - {entity.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  Atualizar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default RondasManagement;